import { Youtube } from "lucide-react";
import { extractYouTubeId, getYouTubeThumbnail } from "@/lib/youtubeHelpers";
import { useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";

interface YouTubeEmbedProps {
  videoUrl: string;
  title: string;
}

export const YouTubeEmbed = ({ videoUrl, title }: YouTubeEmbedProps) => {
  const videoId = extractYouTubeId(videoUrl);
  const thumbnailUrl = getYouTubeThumbnail(videoUrl);
  const watchUrl = videoId ? `https://www.youtube.com/watch?v=${videoId}` : videoUrl;
  
  const [embedFailed, setEmbedFailed] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (!videoId) {
      setEmbedFailed(true);
      setIsChecking(false);
      return;
    }

    const checkEmbeddability = async () => {
      try {
        const oEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
        const response = await fetch(oEmbedUrl);
        
        if (!response.ok) {
          console.log('Video not embeddable - showing premium fallback');
          setEmbedFailed(true);
        }
      } catch (error) {
        console.log('oEmbed check failed - showing premium fallback');
        setEmbedFailed(true);
      } finally {
        setIsChecking(false);
      }
    };

    checkEmbeddability();
  }, [videoId]);

  // Show loading skeleton while checking
  if (isChecking) {
    return (
      <div className="mb-8 overflow-hidden rounded-xl">
        <Skeleton className="w-full" style={{ paddingBottom: '56.25%' }} />
      </div>
    );
  }

  // Premium thumbnail fallback for non-embeddable videos
  if (embedFailed) {
    return (
      <div className="mb-8 overflow-hidden rounded-xl shadow-xl ring-1 ring-white/10">
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute inset-0 group"
          >
            <img
              src={thumbnailUrl}
              alt={title}
              className="h-full w-full object-cover transition-opacity duration-300 group-hover:opacity-90"
            />
            
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/70 transition-opacity duration-300 group-hover:from-black/80 group-hover:to-black/80" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,black_100%)] opacity-40" />
            
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(255,0,0,0.6)]">
                <div className="flex h-20 w-28 items-center justify-center rounded-2xl bg-[#FF0000] shadow-2xl transition-all duration-300 group-hover:bg-[#CC0000]">
                  <div className="ml-1 h-0 w-0 border-l-[18px] border-t-[12px] border-b-[12px] border-l-white border-t-transparent border-b-transparent" />
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-6 left-6 right-6 flex justify-center">
              <div className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-black/40 px-6 py-3 backdrop-blur-md transition-all duration-200 hover:scale-105 hover:bg-black/60 hover:shadow-lg">
                <Youtube className="h-5 w-5 text-[#FF0000]" />
                <span className="font-semibold text-white">Watch on YouTube</span>
              </div>
            </div>
          </a>
        </div>
      </div>
    );
  }

  // Show normal iframe embed for embeddable videos
  return (
    <div className="mb-8 overflow-hidden rounded-xl shadow-xl ring-1 ring-white/10">
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube.com/embed/${videoId}?modestbranding=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
};
